import type { ColDef, IRowNode, SortComparatorFn, SortType, ValueFormatterParams } from 'ag-grid-community';
import Big from 'big.js';
import { get, isNil } from 'lodash-es';

import type { AmountObject } from '../../../utils/notional';
import { toBig } from '../../../utils/number';
import { getCellDisplayValue } from '../../AgGrid/agGridGetCellValue';
import type { BlotterTableContextProps } from '../BlotterTableContext';
import { getAgGridColId } from '../columns/getAgGridColId';
import type { Column } from '../columns/types';
import { getGridApiFromNode, isGridApiReady } from '../utils';

export function dateStringComparator(a: string | undefined, b: string | undefined) {
  if (!a && !b) {
    return 0;
  } else if (!a) {
    return -1;
  } else if (!b) {
    return 1;
  }
  return a.localeCompare(b);
}

export function amountObjectComparator(a: AmountObject | undefined, b: AmountObject | undefined) {
  return numericColumnComparator(a?.value, b?.value);
}

type AbsoluteSortComparatorMap = Partial<Record<SortType, SortComparatorFn>>;

function toAbsoluteComparable(value: unknown): unknown {
  if (value == null || value === '') {
    return value;
  }

  return toBig(value)?.abs() ?? value;
}

/** Compare numeric values by magnitude, ignoring sign. */
export function absoluteNumericColumnComparator(a: unknown, b: unknown): number {
  return numericColumnComparator(toAbsoluteComparable(a), toAbsoluteComparable(b));
}

export function asAbsoluteSortComparatorMap(defaultComparator: SortComparatorFn): AbsoluteSortComparatorMap {
  return {
    default: defaultComparator,
    absolute: (valueA, valueB) => absoluteNumericColumnComparator(valueA, valueB),
  };
}

export function resolveAbsoluteSortComparator(
  defaultComparator: ColDef['comparator'],
  supportsAbsoluteSort: boolean | undefined
): ColDef['comparator'] {
  if (!supportsAbsoluteSort || typeof defaultComparator !== 'function') {
    return defaultComparator;
  }

  return asAbsoluteSortComparatorMap(defaultComparator);
}

export function amountObjectAbsoluteSortComparatorMap(
  getNumericValue: (value: AmountObject | undefined) => unknown = value => value?.value
): AbsoluteSortComparatorMap {
  return {
    default: amountObjectComparator,
    absolute: (valueA, valueB) => absoluteNumericColumnComparator(getNumericValue(valueA), getNumericValue(valueB)),
  };
}

export function resolveAmountObjectComparator(
  supportsAbsoluteSort: boolean | undefined,
  getNumericValue: (value: AmountObject | undefined) => unknown = value => value?.value
): ColDef['comparator'] {
  if (!supportsAbsoluteSort) {
    return amountObjectComparator;
  }

  return amountObjectAbsoluteSortComparatorMap(getNumericValue);
}

export function amountObjectValueFormatter({ value }: ValueFormatterParams<unknown, AmountObject>): string {
  if (value == null) {
    return '';
  }
  return value.value;
}

/**
 * Compares to numeric values. Regards a nullish value to be less than a non-nullish value.
 */
export function numericColumnComparator(a: any, b: any) {
  try {
    return Big(a).cmp(b);
  } catch {
    if (isNil(a) && isNil(b)) {
      return 0;
    }
    if (isNil(a)) {
      return -1;
    }
    if (isNil(b)) {
      return 1;
    }
    return a - b;
  }
}

/**
 * A basic string comparator where a null value is regarded as smaller than a non-null value.
 */
export function stringColumnComparator(a?: string, b?: string) {
  if (a == null && b == null) {
    return 0;
  }

  if (a == null) {
    return -1;
  }

  if (b == null) {
    return 1;
  }

  return a.localeCompare(b);
}

/**
 * Given a list of comparison keys to compare entities of type T on,
 * returns a comparator function, comparing on string values
 *
 * For example passing in comparisonKeys: ["MarketAccount", "Currency", "Destination"] will provide a sorting
 * function which sorts items T by first MarketAccount, secondly Currency and thirdly Destination.
 */
export function stringColumnComparatorMultiColumn<T>(comparisonKeys: (keyof T)[]) {
  return (a: T, b: T) => {
    const keyWithDiff = comparisonKeys.find(key => get(a, key) !== get(b, key));
    return keyWithDiff ? stringColumnComparator(get(a, keyWithDiff), get(b, keyWithDiff)) : 0;
  };
}

/**
 * A factory function for creating a comparator which will invoke your column's valueFormatter and then execute the
 * comparator function based on those formatted values (which is what is being displayed to the user).
 *
 * If there is no valueFormatter defined, the comparator fallbacks to the basic stringColumnComparator logic
 * @param column the talos Column config object. Just used to resolve the effective column id
 */
export function getStringFormattedColumnComparator(column: Column): SortComparatorFn {
  return function stringFormattedColumnComparator(valueA, valueB, nodeA, nodeB) {
    const api = getGridApiFromNode(nodeA);
    const agColumn = api?.getColumn(getAgGridColId(column));
    // we only need to compute valueFormatters if the column in question actually has one defined
    const columnHasValueFormatter = agColumn?.getColDef().valueFormatter != null;

    if (isGridApiReady(api) && agColumn && columnHasValueFormatter) {
      const nodeAFormattedValue = getCellDisplayValue({ node: nodeA, column: agColumn, value: valueA, api });
      const nodeBFormattedValue = getCellDisplayValue({ node: nodeB, column: agColumn, value: valueB, api });
      return stringColumnComparator(nodeAFormattedValue ?? valueA, nodeBFormattedValue ?? valueB);
    }

    // else just do the basics
    return stringColumnComparator(valueA, valueB);
  };
}

function toComparableString(value: unknown): string | undefined {
  return value == null ? undefined : String(value);
}

/**
 * Use numeric sorting when at least one raw value is a number and the other is a number or an empty cell.
 * Empty cells are included because numericColumnComparator defines how they sort relative to numbers.
 */
function numericSortIfNumber(valueA: unknown, valueB: unknown): number | undefined {
  const bothValuesAreNumbersOrEmpty =
    (typeof valueA === 'number' || valueA == null) && (typeof valueB === 'number' || valueB == null);
  const atLeastOneValueIsNumber = typeof valueA === 'number' || typeof valueB === 'number';

  if (!bothValuesAreNumbersOrEmpty || !atLeastOneValueIsNumber) {
    return undefined;
  }

  return numericColumnComparator(valueA, valueB);
}

/**
 * Sort by the string `api.getCellValue({ useFormatter: true })` returns, so columns whose
 * formatter changes the visible text (getLabel, prettyName) follow that text.
 * Numeric raw values stay on numeric order. Falls back to the raw values when the grid API is not ready.
 * @param column the talos Column config object. Just used to resolve the effective column id
 */
export function getFormattedCellValueColumnComparator(column: Column): SortComparatorFn {
  const colKey = getAgGridColId(column);
  return (valueA, valueB, nodeA, nodeB) => {
    const numericComparison = numericSortIfNumber(valueA, valueB);
    if (numericComparison !== undefined) {
      return numericComparison;
    }

    const api = getGridApiFromNode(nodeA);
    if (isGridApiReady(api) && nodeA && nodeB) {
      const displayA = api.getCellValue({ rowNode: nodeA, colKey, useFormatter: true });
      const displayB = api.getCellValue({ rowNode: nodeB, colKey, useFormatter: true });
      return stringColumnComparator(toComparableString(displayA), toComparableString(displayB));
    }

    return stringColumnComparator(toComparableString(valueA), toComparableString(valueB));
  };
}

export function displayNameComparator(
  valueA: string | [string] | null | undefined,
  valueB: string | [string] | null | undefined,
  nodeA: IRowNode,
  contextKeys: (keyof BlotterTableContextProps | null)[]
) {
  if (valueA == null || valueB == null) {
    return valueA == null ? 1 : -1;
  }

  const valueAString = typeof valueA === 'string' ? valueA : String(valueA ?? '');
  const valueBString = typeof valueB === 'string' ? valueB : String(valueB ?? '');

  //only apply displayName comparison if the current level's mapping is specified in contextKeys
  if (nodeA.level >= contextKeys.length || contextKeys[nodeA.level] === null) {
    return valueAString.localeCompare(valueBString);
  }

  const contextKey = contextKeys[nodeA.level]!;
  const context = getGridApiFromNode(nodeA)?.getGridOption('context');
  if (context?.current[contextKey] instanceof Map) {
    const contextMap = context?.current[contextKey] as Map<string, { DisplayName?: string }>;
    const displayValueA = contextMap?.get(valueAString)?.DisplayName || valueAString;
    const displayValueB = contextMap?.get(valueBString)?.DisplayName || valueBString;
    return displayValueA.localeCompare(displayValueB);
  } else {
    return valueAString.localeCompare(valueBString);
  }
}
